import { useCallback, useEffect, useState } from "react";
import { Lock, Unlock, Trash2, RotateCcw, Star, Plus } from "lucide-react";
import StageShell from "@/components/StageShell";
import MarketPanels from "@/components/market/MarketPanels";
import HiredPanel from "@/components/market/HiredPanel";
import { useRun } from "@/hooks/useRun";
import { api } from "@/lib/api";

const CATS = ["hard skill", "tool", "soft skill", "domain", "certification"];

function KeywordRow({ k, onPatch }) {
  return (
    <tr className={`border-t border-border ${k.deleted ? "opacity-40" : ""}`} data-testid={`keyword-row-${k.id}`}>
      <td className="px-3 py-2">
        <div className="font-medium">{k.keyword}</div>
        {k.variants?.length > 0 && <div className="text-[11px] text-muted-foreground">{k.variants.join(", ")}</div>}
        {k.junk_p > 0.6 && !k.deleted && <div className="text-[10px] text-amber-400">Likely junk</div>}
      </td>
      <td className="px-3">
        <select value={k.category} onChange={(e) => onPatch(k, { category: e.target.value })} className="bg-transparent text-xs border border-border rounded px-1 py-0.5">
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-3 w-64">
        <div className="flex items-center gap-2">
          <div className="h-2 flex-1 rounded-full bg-slate-800 overflow-hidden" data-testid="keyword-frequency-bar">
            <div className={`h-full freq-bar ${k.must_have ? "bg-blue-500" : "bg-slate-500"}`} style={{ width: `${k.frequency}%` }} />
          </div>
          <span className="font-mono text-xs w-9 text-right">{k.frequency}%</span>
        </div>
      </td>
      <td className="px-3 text-right whitespace-nowrap">
        <button data-testid={`keyword-musthave-${k.id}`} title="Must-have" onClick={() => onPatch(k, { must_have: !k.must_have })}
          className={`p-1.5 rounded ${k.must_have ? "text-blue-300 bg-blue-500/15" : "text-slate-500 hover:text-slate-200"}`}><Star size={14} /></button>
        <button data-testid={`keyword-lock-${k.id}`} title="Lock" onClick={() => onPatch(k, { locked: !k.locked })}
          className={`p-1.5 rounded ${k.locked ? "text-amber-300 bg-amber-500/15" : "text-slate-500 hover:text-slate-200"}`}>{k.locked ? <Lock size={14} /> : <Unlock size={14} />}</button>
        <button data-testid={`keyword-delete-${k.id}`} title={k.deleted ? "Restore" : "Delete"} onClick={() => onPatch(k, { deleted: !k.deleted })}
          className="p-1.5 rounded text-slate-500 hover:text-rose-300">{k.deleted ? <RotateCcw size={14} /> : <Trash2 size={14} />}</button>
      </td>
    </tr>
  );
}

export default function Stage2({ job, refresh }) {
  const [kws, setKws] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [onlyMust, setOnlyMust] = useState(false);
  const [newKw, setNewKw] = useState("");
  const load = useCallback(() => api.get(`/jobs/${job.id}/keywords`).then(({ data }) => setKws(data)), [job.id]);
  useEffect(() => { load(); }, [load]);
  const runHook = useRun(job.id, 2, () => { load(); refresh(); });
  const patch = async (k, upd) => { setKws((prev) => prev.map((x) => (x.id === k.id ? { ...x, ...upd } : x))); await api.patch(`/keywords/${k.id}`, upd); };
  const add = async () => { if (!newKw.trim()) return; await api.post(`/jobs/${job.id}/keywords`, { keyword: newKw, must_have: true }); setNewKw(""); load(); };
  const shown = kws.filter((k) => (showDeleted || !k.deleted) && (!onlyMust || k.must_have));
  const must = kws.filter((k) => k.must_have && !k.deleted).length;

  return (
    <StageShell job={job} stage={2} refresh={refresh} runHook={runHook}
      runs={[{ action: "market_analyze", label: kws.length ? "Re-run analysis" : "Run market analysis", testid: "market-analyze-button" }]}>
      <div className="grid grid-cols-5 gap-5">
        <div className="col-span-3 card-surface p-0 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <div className="font-semibold">Keyword bank</div>
            <span className="text-xs text-muted-foreground" data-testid="keyword-summary">{kws.filter((k) => !k.deleted).length} keywords · {must} must-have · {kws.filter((k) => k.locked && !k.deleted).length} locked</span>
            <label className="ml-auto text-xs flex items-center gap-1"><input type="checkbox" checked={onlyMust} onChange={(e) => setOnlyMust(e.target.checked)} data-testid="keyword-only-must" /> Must-haves only</label>
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="keyword-show-deleted" /> Show deleted</label>
          </div>
          <div className="max-h-[720px] overflow-auto scroll-thin">
            <table className="w-full text-sm" data-testid="keyword-table">
              <tbody>{shown.map((k) => <KeywordRow key={k.id} k={k} onPatch={patch} />)}</tbody>
            </table>
            {!kws.length && <div className="p-10 text-center text-sm text-muted-foreground">Run the market analysis to build the keyword bank.</div>}
          </div>
          <div className="flex gap-2 p-3 border-t border-border">
            <input data-testid="keyword-add-input" value={newKw} onChange={(e) => setNewKw(e.target.value)} placeholder="Add a keyword manually (exact JD wording)"
              className="flex-1 bg-slate-900 border border-border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500" />
            <button data-testid="keyword-add-button" onClick={add} className="px-3 rounded-md bg-slate-800 hover:bg-slate-700"><Plus size={14} /></button>
          </div>
        </div>
        <div className="col-span-2 space-y-5">
          <MarketPanels market={job.market} />
          <HiredPanel job={job} refresh={refresh} />
        </div>
      </div>
    </StageShell>
  );
}
